import { Fragment, useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import { Card, CardHeader, InlineStat } from '../../../components/ui/Card';
import { Badge, DotBadge } from '../../../components/ui/Badge';
import { Input, Select } from '../../../components/ui/Input';
import { InfoTip } from '../../../components/ui/InfoTip';
import { Nil, Table, TGroupRow, THead, TTotalRow, Th, Td, Tr } from '../../../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { useCsmDeclining, useCsmRoster, useCsmReconciliation } from '../../../hooks/useDashboard';
import type { CsmRosterRow } from '../../../api/dashboard';
import { formatDayMonthTime, formatDayMonthYear } from '../../../utils/dateFormat';
import { formatCompact, formatMoney, formatNumber } from '../../../utils/numberFormat';
import { CompanyUsersRow } from './CompanyDetail';
import { relativeDays, shiftDaysISO, todayISO } from './period';
import { AttioCompanyLink } from './attio';
import { bareDomain } from '../../Sales/shared';
import { keptDeclineReason, pctText, triggerOf, type DecliningRow } from './DecliningTab';
import { TOKENS_LABEL } from './decliningGroups';
import { splitUnlinked, type UnlinkedGroup } from './reconciliationGroups';
import { BAND_THRESHOLDS, bandGap, computedBand, HEALTH_BANDS, healthDriversText, manualBand, matchesHealthFilter, type HealthBand, type HealthFilter } from './rosterHealth';

/**
 * The decline detector's verdict, under the health score. Wraps inside the
 * Health column, so a long trigger never runs into MRR (GELITA, 13 Oct).
 */
function DeclineMarker({ row }: { row: DecliningRow | undefined }) {
  if (!row) return null;
  const t = triggerOf(row);
  const what = t ? `${t.metric === 'tokens' ? TOKENS_LABEL : 'Login events'} ${pctText(t.changePct)}, ${t.window}` : null;
  const metric = t ? (t.metric === 'tokens' ? 'tokens' : 'logins') : null;
  const kept = keptDeclineReason(row, t);
  const confirmed = row.severity === 'confirmed';
  return (
    <div
      className={clsx('mt-0.5 cursor-help whitespace-normal text-body-xs font-medium leading-snug', confirmed ? 'text-error' : 'text-warning-dark')}
      title={`${confirmed ? 'Confirmed decline' : 'Early warning'} from the decline detector (At risk tab)${what ? `: ${what}` : ''}${
        kept ? `. Tokens rose over that window, but ${kept}, so it stays a decline` : ''
      }. The score above already takes the decline penalty.`}
    >
      {confirmed ? 'Declining' : 'Early warning'}
      {t && (
        <span className="font-normal">
          {' '}
          · {metric} {pctText(t.changePct)} {t.window}
          {kept && <>, {kept}</>}
        </span>
      )}
    </div>
  );
}

const BAND_DOT: Record<HealthBand, 'active' | 'warning' | 'error'> = {
  Healthy: 'active',
  'At risk': 'warning',
  Critical: 'error',
};

/**
 * One Health cell, one story: the computed CS health score and its band lead
 * (the dot is the band's status colour); the CSM's own Attio label sits
 * underneath, with "disagrees" when it is in another band (GELITA: Good in
 * Attio, 55 At risk computed).
 */
function HealthCell({ row, decline }: { row: CsmRosterRow; decline: DecliningRow | undefined }) {
  // No platform user at all (EMIS Health, StormHarvester): nothing to score, so no "0 Critical".
  const noUsers = row.totalMembers === 0;
  const h = noUsers ? null : row.computedHealth;
  const band = computedBand(h?.tier);
  const gap = bandGap(row);
  const rollUp =
    h && (h.scoredEntities ?? 1) + (h.idleEntitiesExcluded ?? 0) > 1
      ? ` Built from ${h.scoredEntities} of ${(h.scoredEntities ?? 0) + (h.idleEntitiesExcluded ?? 0)} platform entities, weighted by tokens; ${
          h.idleEntitiesExcluded ? `${h.idleEntitiesExcluded} with no tokens in 30 days left out.` : 'none left out.'
        }`
      : '';
  return (
    <div className="min-w-0">
      {h && band ? (
        <span
          className="cursor-help"
          title={[
            `Computed CS health score ${h.score}/100: ${band} (${BAND_THRESHOLDS}). Scored ${formatDayMonthYear(`${h.snapshotDate}T00:00:00Z`)} by the daily CSM snapshot.`,
            healthDriversText(h.drivers) ?? 'From usage and call sentiment, minus any decline penalty.',
            rollUp.trim(),
          ]
            .filter(Boolean)
            .join('\n')}
        >
          <DotBadge status={BAND_DOT[band]}>
            <span className="font-medium tabular-nums text-text-primary">{h.score}</span>
            <span className="text-text-secondary">{band}</span>
          </DotBadge>
        </span>
      ) : (
        <span
          className="cursor-help text-body-xs text-text-tertiary"
          title={
            noUsers
              ? 'No platform user is linked to this company, so there is no usage to score.'
              : 'The daily CSM snapshot has not scored this company yet. Every company on the CSM list with platform users is scored from the next snapshot on.'
          }
        >
          {noUsers ? 'Not scored: no users' : 'Not scored yet'}
        </span>
      )}
      <div className="text-body-xs text-text-tertiary">
        {row.health ? (
          <span title="The CSM's own call on the Attio CS list entry">Attio: {row.health}</span>
        ) : (
          <span title="The Health field on this company's Attio CS list entry is empty: the CSM hasn't set it.">Attio: not set</span>
        )}
        {!noUsers && gap !== null && gap > 0 && (
          <span
            className="cursor-help font-medium text-warning-dark"
            title={`The CSM's Attio call (${row.health}, ${manualBand(row.health)}) is ${gap === 1 ? 'one band' : 'two bands'} away from the computed score (${band}). Check which one is right, and update Attio if the CSM's call is out of date.`}
          >
            {' '}
            · disagrees
          </span>
        )}
      </div>
      <DeclineMarker row={decline} />
    </div>
  );
}

/** Roster metrics are 30-day figures, so the detail panel uses the same window. */
const ROSTER_WINDOW_DAYS = 30;
const ROSTER_PERIOD_TEXT = 'last 30 days';

const TABLE_COLUMN_COUNT = 10;
/**
 * Fixed widths so the columns don't shift when the population switch changes the
 * rows, and sized so Company keeps about 190px at 1440px (the old widths summed
 * to more than the card and squeezed Company under CSM).
 */
const ROSTER_COLS = ['2rem', 'auto', '9rem', '6.5rem', '12.5rem', '5.5rem', '7rem', '6.5rem', '6rem', '2.25rem'];

function orNil(value: string) {
  return value === '' ? <Nil /> : value;
}

function LastActive({ iso }: { iso: string | null }) {
  if (!iso) return <Nil />;
  return <span title={formatDayMonthTime(iso)}>{relativeDays(iso, formatDayMonthYear)}</span>;
}

/** Classic + aOS, with the split on hover. */
function Tokens({ value, aos }: { value: number; aos?: number }) {
  const title =
    aos === undefined
      ? formatNumber(value)
      : `${formatNumber(value)} = ${formatNumber(value - aos)} Classic + ${formatNumber(aos)} aOS`;
  return <span title={title}>{orNil(formatCompact(value))}</span>;
}

/** MRR from the Finance MRR engine; "·" when the book has none for the company. Never ARR. */
function Mrr({ value }: { value: number | null | undefined }) {
  return value == null || value === 0 ? <Nil /> : <>{formatMoney(value)}</>;
}

function Chevron({ open, hidden }: { open: boolean; hidden?: boolean }) {
  return (
    <ChevronRight
      className={clsx('h-4 w-4 text-text-muted transition-transform', open && 'rotate-90', hidden && 'invisible')}
      aria-hidden
    />
  );
}

/** The same per-company detail panel for roster rows and unlinked platform companies. */
function DetailRow({
  companyIds,
  activityWindow,
  tokens,
}: {
  companyIds: string[];
  activityWindow: { from: string; to: string };
  /** The roster row's 30-day tokens, so the panel shows the same total as the row. */
  tokens?: { total: number; aos: number };
}) {
  return (
    <tr className="border-b border-border-light bg-surface-secondary/40">
      <td colSpan={TABLE_COLUMN_COUNT} className="p-4">
        <div className="space-y-4">
          {/* Several platform entities: the roster-wide total leads, then each entity's own panel. */}
          {tokens && companyIds.length > 1 && (
            <InlineStat
              label={`Tokens, ${ROSTER_PERIOD_TEXT}`}
              value={formatCompact(tokens.total) || '0'}
              sub={`${formatCompact(tokens.total - tokens.aos) || '0'} Classic + ${formatCompact(tokens.aos) || '0'} aOS · customer users, all ${companyIds.length} entities`}
              size="sm"
            />
          )}
          {companyIds.map((id) => (
            <div key={id}>
              {companyIds.length > 1 && <p className="mb-2 text-body-xs text-text-tertiary">Platform entity {id}</p>}
              <CompanyUsersRow
                companyId={id}
                activityWindow={activityWindow}
                periodText={ROSTER_PERIOD_TEXT}
                leadTokens={companyIds.length === 1 ? tokens : undefined}
              />
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

/**
 * Which roster entries count: the KPI strip and the table read the same population.
 * - `customers`: active customers only (Attio Type Customer, not churned)
 * - `withChurned`: active customers plus every churned company, whatever its type
 * - `everyone`: every entry on the CS list (prospects, partners…)
 */
export type RosterPopulation = 'customers' | 'withChurned' | 'everyone';

export const ROSTER_POPULATION_OPTIONS: readonly { id: RosterPopulation; label: string }[] = [
  { id: 'customers', label: 'Customers' },
  { id: 'withChurned', label: '+ Churned' },
  { id: 'everyone', label: 'Everyone' },
];

/**
 * Churned = the CS list stage is "Churned", or the Attio company Type is
 * "ExCustomer". Most churned companies have their Type flipped to ExCustomer
 * (and stage Churned); a few keep Type Customer with stage Churned. Either
 * signal on its own is enough.
 */
export function isChurned(row: CsmRosterRow): boolean {
  return row.lifecycleStage === 'Churned' || row.companyType === 'ExCustomer';
}

export function inRosterPopulation(row: CsmRosterRow, population: RosterPopulation): boolean {
  if (population === 'everyone') return true;
  if (isChurned(row)) return population === 'withChurned';
  return row.companyType === 'Customer';
}

/** "Everyone" is only worth offering when it lists rows that "+ Churned" doesn't. */
export function everyoneAddsRows(rows: readonly CsmRosterRow[]): boolean {
  return rows.some((r) => !inRosterPopulation(r, 'withChurned'));
}

/**
 * The roster plus the population filter. Always fetches every Attio type and
 * filters here, so the type and lifecycle switches are applied in one place and
 * the KPI strip can count exactly the rows the table shows.
 */
export function useRosterPopulation(requested: RosterPopulation) {
  const roster = useCsmRoster(true);
  const showEveryone = useMemo(() => everyoneAddsRows(roster.data?.rows ?? []), [roster.data]);
  // "Everyone" falls back to "+ Churned" when it would list the same rows.
  const population: RosterPopulation = requested === 'everyone' && !showEveryone ? 'withChurned' : requested;
  const rows = useMemo(
    () => (roster.data?.rows ?? []).filter((r) => inRosterPopulation(r, population)),
    [roster.data, population],
  );
  return { roster, rows, population, showEveryone };
}

/** One folded list of platform companies the Attio list doesn't carry: 15 a page, Classic + aOS tokens like the roster. */
function UnlinkedSection({
  title,
  note,
  badge,
  rows,
  activityWindow,
  expandedKey,
  toggle,
  open,
  onOpen,
}: {
  title: string;
  note?: string;
  badge?: 'warning';
  rows: UnlinkedGroup[];
  activityWindow: { from: string; to: string };
  expandedKey: string | null;
  toggle: (key: string) => void;
  open: boolean;
  onOpen: () => void;
}) {
  const { pageItems, ...paging } = usePagination(rows);
  // Old API: no aOS figure, so the header says Classic only instead of implying a sum.
  const hasAos = rows.every((r) => r.aosTokens30d !== undefined);
  // An API without the aOS field, and no Classic tokens either: the column would be zero on every row.
  const showTokens = hasAos || rows.some((r) => r.tokens30d > 0);
  return (
    <div className="space-y-2">
      <button type="button" onClick={onOpen} className="flex items-center gap-2 text-left" aria-expanded={open}>
        <Chevron open={open} />
        <h4 className="text-body-sm font-medium text-text-secondary">{title}</h4>
        <Badge variant={badge ?? 'default'}>{rows.length}</Badge>
        {note && <span className="text-body-xs text-text-tertiary">{note}</span>}
      </button>
      {open && (
        <>
          <Table minWidth={760} colWidths={showTokens ? ['2rem', 'auto', '6rem', '6rem', '7rem', '7.5rem'] : ['2rem', 'auto', '6rem', '6rem', '7.5rem']}>
            <THead>
              <tr className="border-b border-border-light">
                <Th />
                <Th>Company</Th>
                <Th align="right">Members</Th>
                <Th align="right">Active 30d</Th>
                {showTokens && (
                  <Th
                    align="right"
                    title={hasAos ? 'Classic + aOS tokens by customer users, last 30 days: the roster’s figure' : 'Classic tokens only, last 30 days (this API does not send aOS tokens)'}
                  >
                    Tokens 30d
                  </Th>
                )}
                <Th align="right" className="!pr-3">Last active</Th>
              </tr>
            </THead>
            <tbody>
              {pageItems.map((company) => {
                const key = `prod:${company.key}`;
                const isExpanded = expandedKey === key;
                return (
                  <Fragment key={key}>
                    <Tr onClick={() => toggle(key)}>
                      <Td className="pr-2">
                        <Chevron open={isExpanded} />
                      </Td>
                      <Td>
                        <div className="font-medium text-text-primary">{company.name}</div>
                        {company.domain && <div className="text-body-xs text-text-tertiary">{bareDomain(company.domain)}</div>}
                      </Td>
                      <Td align="right" className="tabular-nums">
                        {formatNumber(company.totalMembers)}
                      </Td>
                      <Td align="right" className="tabular-nums">
                        {formatNumber(company.activeMembers30d)}
                      </Td>
                      {showTokens && (
                        <Td align="right" className="tabular-nums">
                          <Tokens value={company.tokens30d} aos={company.aosTokens30d} />
                        </Td>
                      )}
                      <Td align="right" className="whitespace-nowrap text-text-secondary !pr-3">
                        <LastActive iso={company.lastActiveAt} />
                      </Td>
                    </Tr>
                    {isExpanded && <DetailRow companyIds={company.prodCompanyIds} activityWindow={activityWindow} />}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
          <Pagination {...paging} onPage={paging.setPage} />
        </>
      )}
    </div>
  );
}

export function RosterTab({
  population,
  onPopulationChange,
}: {
  population: RosterPopulation;
  onPopulationChange: (next: RosterPopulation) => void;
}) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [csm, setCsm] = useState('');
  const [health, setHealth] = useState<HealthFilter>('');
  const [showUnlinked, setShowUnlinked] = useState(false);
  const [showPersonal, setShowPersonal] = useState(false);
  const { roster, rows: allRows, population: effectivePopulation, showEveryone } = useRosterPopulation(population);
  const populationOptions = useMemo(
    () => ROSTER_POPULATION_OPTIONS.filter((o) => o.id !== 'everyone' || showEveryone),
    [showEveryone],
  );
  const reconciliation = useCsmReconciliation();
  const declining = useCsmDeclining();
  const declineByEntry = useMemo(
    () => new Map(((declining.data?.companies ?? []) as DecliningRow[]).map((c) => [c.entryId, c])),
    [declining.data],
  );
  const rosterRows = roster.data?.rows;
  const { companies: unlinked, personal: personalWorkspaces, secondWorkspaces } = useMemo(
    () => splitUnlinked(reconciliation.data?.prodCompaniesNotOnList ?? [], (rosterRows ?? []).filter((r) => !isChurned(r)).map((r) => ({ name: r.companyName, domain: r.domain }))),
    [reconciliation.data, rosterRows],
  );
  const unmatched = reconciliation.data?.unmatchedCustomers ?? [];

  const csmOptions = useMemo(
    () => [...new Set(allRows.map((r) => r.primaryCsmName).filter((n): n is string => !!n))].sort(),
    [allRows],
  );
  const disagreeCount = useMemo(() => allRows.filter((r) => !isChurned(r) && (bandGap(r) ?? 0) > 0).length, [allRows]);
  const unscoredCount = useMemo(() => allRows.filter((r) => !isChurned(r) && matchesHealthFilter(r, 'unscored')).length, [allRows]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter(
      (r) =>
        (!q || (r.companyName ?? '').toLowerCase().includes(q) || (r.domain ?? '').toLowerCase().includes(q)) &&
        (!csm || r.primaryCsmName === csm) &&
        matchesHealthFilter(r, health),
    );
  }, [allRows, search, csm, health]);
  // Churned rows carry no usage (every metric is "·"), so they fold into one group row below the live customers.
  const liveRows = useMemo(() => rows.filter((r) => !isChurned(r)), [rows]);
  const churnedRows = useMemo(() => rows.filter(isChurned), [rows]);
  const [showChurned, setShowChurned] = useState(false);
  const pager = usePagination(liveRows);
  // Every live row that passes the filters, not just the current page.
  const totals = useMemo(
    () => ({
      mrr: liveRows.reduce((n, r) => n + (r.mrr ?? 0), 0),
      members: liveRows.reduce((n, r) => n + r.totalMembers, 0),
      active: liveRows.reduce((n, r) => n + r.activeMembers30d, 0),
      tokens: liveRows.reduce((n, r) => n + r.tokens30d, 0),
    }),
    [liveRows],
  );
  const churnedPager = usePagination(churnedRows);

  const activityWindow = useMemo(() => ({ from: shiftDaysISO(ROSTER_WINDOW_DAYS), to: todayISO() }), []);
  const toggle = (key: string) => setExpandedKey((current) => (current === key ? null : key));

  return (
    <div className="space-y-6">
      <Card padding="sm">
        <CardHeader
          title="Customer roster"
          description={
            roster.data?.lastSyncedAt
              ? `From the Attio CS master list · synced ${formatDayMonthTime(roster.data.lastSyncedAt)} · open a row for users and tokens${declining.isError ? ' · decline markers unavailable: the declining list couldn’t load' : ''}`
              : `From the Attio CS master list · open a row for users and tokens${declining.isError ? ' · decline markers unavailable: the declining list couldn’t load' : ''}`
          }
          action={
            <InfoTip label="What the usage columns count">
              <span className="font-medium">Health</span> leads with the computed CS health score (daily CSM snapshot:
              usage and call sentiment, minus any decline penalty). For a company with several platform entities it is
              the token-weighted score of the entities with usage. The CSM’s own Attio call sits underneath, marked
              “disagrees” when it is in another band.{' '}
              <span className="font-medium">Users</span> counts external users only, active in the last 30 days of all
              members, across every platform entity: SEQUESTO staff (@sequesto.com) are left out.{' '}
              <span className="font-medium">Tokens 30d</span> is the CSM space’s one token figure: Classic + aOS over the
              30 days ending today. aOS tokens used by SEQUESTO staff are left out; Classic tokens carry no user, so they
              all count. Declining and At risk use the same figure; Usage splits it by platform.{' '}
              <span className="font-medium">MRR</span> is this month’s figure from the Finance MRR engine (sheet overrides
              applied), the same as Finance and Customers. It is never the Attio ARR.
            </InfoTip>
          }
        />
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SegmentedControl
            options={populationOptions}
            value={effectivePopulation}
            onChange={onPopulationChange}
            ariaLabel="Which companies to count"
          />
          <div className="w-44">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search company"
              className="px-3 py-1.5 text-body-xs"
            />
          </div>
          <Select size="sm" value={csm} onChange={(e) => setCsm(e.target.value)} aria-label="CSM">
            <option value="">All CSMs</option>
            {csmOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
          <Select size="sm" value={health} onChange={(e) => setHealth(e.target.value as HealthFilter)} aria-label="Health">
            <option value="">Any health</option>
            {HEALTH_BANDS.map((b) => (
              <option key={b} value={b}>
                {b} (computed)
              </option>
            ))}
            {disagreeCount > 0 && <option value="disagrees">Attio disagrees ({formatNumber(disagreeCount)})</option>}
            {unscoredCount > 0 && <option value="unscored">Not scored ({formatNumber(unscoredCount)})</option>}
          </Select>
        </div>

        {roster.isLoading ? (
          <LoadingState rows={6} />
        ) : roster.isError ? (
          <ErrorState title="Couldn’t load the roster" error={roster.error} onRetry={() => roster.refetch()} />
        ) : (roster.data?.rows ?? []).length === 0 ? (
          <EmptyState
            title="Roster is empty"
            description="The Attio CSM list has not been synced yet. Run the “Sync CSM Dashboard” workflow once."
          />
        ) : rows.length === 0 ? (
          <EmptyState title="No customers match these filters" compact />
        ) : (
          <>
            <Table minWidth={1000} colWidths={ROSTER_COLS}>
              <THead>
                <tr className="border-b border-border-light">
                  <Th className="w-6" />
                  <Th>Company</Th>
                  <Th>CSM</Th>
                  <Th title="Blank means Adoption, the usual stage; any other stage is shown">Lifecycle</Th>
                  <Th title="The computed CS health score (0 to 100, daily CSM snapshot) and its band, with the CSM's own Attio call underneath">Health</Th>
                  <Th align="right" title="This month, Finance MRR engine">MRR</Th>
                  <Th align="right" title="External users (no @sequesto.com) with a login in the last 30 days, of all external members, across every platform entity of the company">
                    Users
                    <span className="block text-body-xs font-normal normal-case tracking-normal text-text-tertiary">active of all</span>
                  </Th>
                  <Th align="right" title="Classic + aOS tokens, last 30 days">
                    Tokens 30d
                    <span className="block text-body-xs font-normal normal-case tracking-normal text-text-tertiary">Classic + aOS</span>
                  </Th>
                  <Th align="right">Last active</Th>
                  <Th className="!pr-0">
                    <span className="sr-only">Attio</span>
                  </Th>
                </tr>
              </THead>
              <tbody>
                {pager.pageItems.map((row: CsmRosterRow) => {
                  const key = `roster:${row.entryId}`;
                  const isExpanded = expandedKey === key;
                  const canExpand = row.matchedProdCompanyIds.length > 0;
                  const churned = isChurned(row);
                  return (
                    <Fragment key={key}>
                      <Tr onClick={canExpand ? () => toggle(key) : undefined} className={clsx(churned && 'opacity-50')}>
                        <Td className="pr-2">
                          <Chevron open={isExpanded} hidden={!canExpand} />
                        </Td>
                        <Td>
                          {/* Wraps, so a pill never runs into the CSM column (EMIS Health). */}
                          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-medium text-text-primary">{row.companyName ?? <Nil />}</span>
                            {/* Churned rows already say so in Lifecycle; the unlinked dot is only news for live customers. */}
                            {row.matchedProdCompanyIds.length === 0 && !churned && (
                              <span
                                className="h-2 w-2 flex-none rounded-full bg-error"
                                title="Not linked to a platform company: set “[PROD] Company ID” in Attio"
                              />
                            )}
                            {row.matchedProdCompanyIds.length > 1 && (
                              <span className="text-body-xs text-text-tertiary">
                                {row.matchedProdCompanyIds.length} entities
                              </span>
                            )}
                            {/* A live customer the Finance MRR engine bills nothing for is an exception (EMIS Health). */}
                            {!churned && !roster.data?.mrr?.error && !row.mrr && (
                              <span title="On the CSM list as a live customer, but the Finance MRR engine has no MRR for it this month: check the contract in Odoo, or the list stage in Attio">
                                <Badge variant="warning">No live contract</Badge>
                              </span>
                            )}
                          </div>
                          {row.domain && <div className="text-body-xs text-text-tertiary">{bareDomain(row.domain)}</div>}
                        </Td>
                        <Td className="whitespace-nowrap text-text-secondary">{row.primaryCsmName ?? <Nil />}</Td>
                        <Td>{!row.lifecycleStage ? <Nil /> : row.lifecycleStage === 'Adoption' ? null : <Badge>{row.lifecycleStage}</Badge>}</Td>
                        <Td>
                          {/* No users or no contract (EMIS Health): nothing live to decline, so no decline message; "No live contract" sits next to the name. */}
                          <HealthCell
                            row={row}
                            decline={row.totalMembers === 0 || (!roster.data?.mrr?.error && !row.mrr) ? undefined : declineByEntry.get(row.entryId)}
                          />
                        </Td>
                        <Td align="right" className="tabular-nums">
                          <Mrr value={row.mrr} />
                        </Td>
                        {/* A churned company has no usage to report: Nil, not a row of zeros. */}
                        <Td align="right" className="tabular-nums whitespace-nowrap">
                          {churned ? (
                            <Nil />
                          ) : (
                            <span
                              title={`${formatNumber(row.activeMembers30d)} of ${formatNumber(row.totalMembers)} external members logged in during the last 30 days${
                                row.matchedProdCompanyIds.length > 1 ? `, across all ${row.matchedProdCompanyIds.length} platform entities (Usage lists each entity on its own row)` : ''
                              }`}
                            >
                              <span className="text-text-primary">{formatNumber(row.activeMembers30d)}</span>
                              <span className="text-text-tertiary"> of {formatNumber(row.totalMembers)}</span>
                            </span>
                          )}
                        </Td>
                        <Td align="right" className="tabular-nums">
                          {churned ? <Nil /> : <Tokens value={row.tokens30d} aos={row.aosTokens30d} />}
                        </Td>
                        <Td align="right" className="whitespace-nowrap text-text-secondary">
                          <LastActive iso={row.lastActiveAt} />
                        </Td>
                        <Td align="right" className="!pr-0">
                          <AttioCompanyLink recordId={row.attioRecordId} name={row.companyName} />
                        </Td>
                      </Tr>
                      {isExpanded && (
                        <DetailRow
                          companyIds={row.matchedProdCompanyIds}
                          activityWindow={activityWindow}
                          tokens={row.aosTokens30d === undefined ? undefined : { total: row.tokens30d, aos: row.aosTokens30d }}
                        />
                      )}
                    </Fragment>
                  );
                })}
                {churnedRows.length > 0 && (
                  <TGroupRow
                    colSpan={TABLE_COLUMN_COUNT}
                    label={
                      <button
                        type="button"
                        onClick={() => setShowChurned((v) => !v)}
                        aria-expanded={showChurned}
                        className="flex items-center gap-2 text-left"
                      >
                        <Chevron open={showChurned} />
                        Churned
                      </button>
                    }
                    summary={`${formatNumber(churnedRows.length)} ${churnedRows.length === 1 ? 'company' : 'companies'} · no usage to report${showChurned ? '' : ' · click to list'}`}
                  />
                )}
                {showChurned &&
                  churnedPager.pageItems.map((row: CsmRosterRow) => (
                    <Tr key={`churned:${row.entryId}`} className="opacity-60">
                      <Td className="pr-2" />
                      <Td>
                        <span className="font-medium text-text-primary">{row.companyName ?? <Nil />}</span>
                        {row.domain && <div className="text-body-xs text-text-tertiary">{bareDomain(row.domain)}</div>}
                      </Td>
                      <Td className="whitespace-nowrap text-text-secondary">{row.primaryCsmName ?? <Nil />}</Td>
                      <Td>{row.lifecycleStage ? <Badge>{row.lifecycleStage}</Badge> : <Nil />}</Td>
                      <Td colSpan={4} className="text-body-xs text-text-tertiary">
                        {row.mrr ? `Still billing: MRR ${formatMoney(row.mrr)}` : ''}
                      </Td>
                      <Td align="right" className="whitespace-nowrap text-text-secondary">
                        <LastActive iso={row.lastActiveAt} />
                      </Td>
                      <Td align="right" className="!pr-0">
                        <AttioCompanyLink recordId={row.attioRecordId} name={row.companyName} />
                      </Td>
                    </Tr>
                  ))}
              </tbody>
              {liveRows.length > 1 && (
                <tfoot>
                  <TTotalRow>
                    <Td />
                    <Td>
                      Total · {formatNumber(liveRows.length)} {liveRows.length === 1 ? 'company' : 'companies'}
                    </Td>
                    <Td />
                    <Td />
                    <Td />
                    <Td align="right" className="tabular-nums">
                      {roster.data?.mrr?.error ? <Nil /> : formatMoney(totals.mrr)}
                    </Td>
                    <Td align="right" className="tabular-nums whitespace-nowrap">
                      {formatNumber(totals.active)}
                      <span className="font-normal text-text-tertiary"> of {formatNumber(totals.members)}</span>
                    </Td>
                    <Td align="right" className="tabular-nums">
                      <Tokens value={totals.tokens} />
                    </Td>
                    <Td />
                    <Td />
                  </TTotalRow>
                </tfoot>
              )}
            </Table>
            <Pagination
              page={pager.page}
              pageCount={pager.pageCount}
              total={pager.total}
              pageSize={pager.pageSize}
              onPage={pager.setPage}
            />
            {showChurned && churnedPager.pageCount > 1 && (
              <Pagination
                page={churnedPager.page}
                pageCount={churnedPager.pageCount}
                total={churnedPager.total}
                pageSize={churnedPager.pageSize}
                onPage={churnedPager.setPage}
              />
            )}
          </>
        )}
      </Card>

      {reconciliation.isError && (
        <ErrorState
          title="Couldn’t load the Attio ↔ platform reconciliation"
          error={reconciliation.error}
          onRetry={() => reconciliation.refetch()}
        />
      )}

      {(unmatched.length > 0 || secondWorkspaces.length > 0 || unlinked.length > 0 || personalWorkspaces.length > 0) && (
        <Card padding="sm" className="space-y-4">
          <CardHeader
            title="Reconciliation"
            description="Where the Attio list and the platform disagree. Every list here is empty when they are aligned. Paying companies missing from the list are flagged at the top of the page."
          />

          {unmatched.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h4 className="text-body-sm font-medium text-text-secondary">Attio customers without a platform match</h4>
                <Badge variant="error">{unmatched.length}</Badge>
                <InfoTip>Fix: set the “[PROD] Company ID” (OSmatching) field on the company in Attio.</InfoTip>
              </div>
              <ul className="divide-y divide-border-light text-body-sm">
                {unmatched.map((c) => (
                  <li key={c.entryId} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                    <span className="text-text-primary">{c.companyName ?? c.attioRecordId}</span>
                    <span className="text-body-xs text-text-tertiary">{c.matchReason ?? 'No match reason recorded'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {secondWorkspaces.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h4 className="text-body-sm font-medium text-text-secondary">Listed customers with a second platform workspace</h4>
                <Badge variant="warning">{secondWorkspaces.length}</Badge>
                <InfoTip>Fix: add the workspace’s ID to the company’s “[PROD] Company ID” (OSmatching) field in Attio, so its usage counts on the roster.</InfoTip>
              </div>
              <ul className="divide-y divide-border-light text-body-sm">
                {secondWorkspaces.map((g) => (
                  <li key={g.key} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                    <span className="text-text-primary">{g.listedAs}</span>
                    <span className="text-body-xs text-text-tertiary">
                      “{g.name}” ({bareDomain(g.domain)}), {formatNumber(g.totalMembers)} {g.totalMembers === 1 ? 'member' : 'members'}, not linked to the list entry
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {unlinked.length > 0 && (
            <UnlinkedSection
              title="Platform companies not on the Attio list"
              badge="warning"
              rows={unlinked}
              activityWindow={activityWindow}
              expandedKey={expandedKey}
              toggle={toggle}
              open={showUnlinked}
              onOpen={() => setShowUnlinked((v) => !v)}
            />
          )}

          {personalWorkspaces.length > 0 && (
            <UnlinkedSection
              title="Personal workspaces"
              note="named after one of their own members"
              rows={personalWorkspaces}
              activityWindow={activityWindow}
              expandedKey={expandedKey}
              toggle={toggle}
              open={showPersonal}
              onOpen={() => setShowPersonal((v) => !v)}
            />
          )}
        </Card>
      )}
    </div>
  );
}
